import { createContext, useContext, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";

const ProductsContext = createContext();

const SESSION_MENU_KEY = "products_sidemenu";
const SESSION_BREADCRUMBS_KEY = "products_breadcrumbs";
const SESSION_OPEN_CATEGORIES_KEY = "sidemenu_open_categories";

const readSession = (key) => {
  try {
    const saved = sessionStorage.getItem(key);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const writeSession = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

export const ProductsProvider = ({ children }) => {
  const location = useLocation();
  const [filters, setFilters] = useState({});
  const [sideMenuItems, setSideMenuItems] = useState(
    () => readSession(SESSION_MENU_KEY) ?? []
  );
  const [itemsLength, setItemsLength] = useState(0);
  const [breadcrumbs, setBreadcrumbs] = useState(
    () => readSession(SESSION_BREADCRUMBS_KEY) ?? []
  );
  const [openCategories, setOpenCategories] = useState(
    () => new Set(readSession(SESSION_OPEN_CATEGORIES_KEY) ?? [])
  );
  // Abrir/fechar filhos só faz sentido nos 2 primeiros níveis da árvore de
  // produtos (listagem de gamas e gama); abaixo disso o SideMenu mostra os
  // filhos sempre expandidos.
  const [menuCollapsible, setMenuCollapsible] = useState(true);

  const updateFilters = (updated) =>
    setFilters((prev) => ({ ...prev, ...updated }));

  // forPath: pathname (com locale) para o qual "items" foi calculado. Se a
  // navegação já mudou de página entretanto (ex: página anterior ainda tinha
  // um fetch em curso quando se navegou para outro nível), o pedido é ignorado
  // para não sobrepor o menu com dados de uma página já abandonada.
  const updateSideMenuItems = useCallback(
    (items, forPath, { collapsible = true } = {}) => {
      if (forPath && forPath !== location.pathname) return;

      setSideMenuItems((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(items)) return prev;
        writeSession(SESSION_MENU_KEY, items);
        return items;
      });
      setMenuCollapsible(collapsible);
    },
    [location.pathname]
  );

  const updateItemsLength = useCallback((length) => {
    setItemsLength((prev) => (prev === length ? prev : length));
  }, []);

  const updateBreadcrumbs = useCallback((crumbs) => {
    setBreadcrumbs((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(crumbs)) return prev;
      writeSession(SESSION_BREADCRUMBS_KEY, crumbs);
      return crumbs;
    });
  }, []);

  const toggleCategory = useCallback((key) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      writeSession(SESSION_OPEN_CATEGORIES_KEY, [...next]);
      return next;
    });
  }, []);

  // Garante que a categoria da página atual aparece expandida na sidebar,
  // sem depender de o utilizador a ter aberto manualmente antes.
  const ensureCategoryOpen = useCallback((key) => {
    setOpenCategories((prev) => {
      if (!key || prev.has(key)) return prev;
      const next = new Set(prev).add(key);
      writeSession(SESSION_OPEN_CATEGORIES_KEY, [...next]);
      return next;
    });
  }, []);

  return (
    <ProductsContext.Provider
      value={{
        filters,
        updateFilters,
        itemsLength,
        updateItemsLength,
        sideMenuItems,
        updateSideMenuItems,
        breadcrumbs,
        updateBreadcrumbs,
        openCategories,
        toggleCategory,
        ensureCategoryOpen,
        menuCollapsible,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export const useProductsContext = () => useContext(ProductsContext);
